import { useParams, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowUpRight, Train, Lightbulb, Ticket } from "lucide-react";
import { Helmet } from "react-helmet-async";
import { Reveal } from "@/components/motion";
import { CityMap } from "@/components/CityMap";
import { GygTourBand } from "@/components/GetYourGuide";
import { TRIPS, getTrip } from "@/data/trips";
import { Seo } from "@/components/Seo";
import { ShareButton } from "@/components/ShareButton";
import { ShareBar } from "@/components/ShareBar";

export default function TripDetail() {
  const { id } = useParams();
  const trip = getTrip(id);
  if (!trip) return <Navigate to="/" replace />;

  const idx = TRIPS.findIndex((t) => t.id === id);
  const prev = idx > 0 ? TRIPS[idx - 1] : null;
  const next = idx >= 0 && idx < TRIPS.length - 1 ? TRIPS[idx + 1] : null;

  const blurbText = trip.blurb.replace(/\n\n/g, " ");

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "TouristAttraction",
    name: trip.name,
    description: blurbText,
    image: trip.image,
    address: { "@type": "PostalAddress", addressCountry: trip.id === "malmo" ? "SE" : "DK" },
    geo: { "@type": "GeoCoordinates", latitude: trip.coords[0], longitude: trip.coords[1] },
    isAccessibleForFree: true,
  };

  return (
    <main data-testid="trip-detail-page" className="pt-16 md:pt-20">
      <Seo
        title={`${trip.name} — Day Trip from Copenhagen`}
        description={blurbText}
        path={`/trips/${trip.id}`}
        image={trip.image}
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      {/* hero */}
      <section className="relative border-b border-zinc-200">
        <div className="relative h-[46vh] min-h-[340px] overflow-hidden">
          {trip.image ? (
            <img src={trip.image} alt={`${trip.name} — a day trip from Copenhagen`} className="absolute inset-0 w-full h-full object-cover" />
          ) : (
            <div className="absolute inset-0" style={{ backgroundColor: trip.accent }} />
          )}
          <div className="absolute inset-0 bg-black/45" />
          <div className="relative max-w-[1400px] mx-auto px-5 md:px-10 h-full flex flex-col justify-end pb-10">
            <div className="self-start flex items-center gap-3 mb-6">
              <Link
                to="/trips"
                data-testid="trip-back"
                className="inline-flex items-center gap-2 font-body font-700 text-sm bg-white text-black px-5 py-2.5 rounded-full border border-zinc-200 hover:bg-[#F9F8F6] transition-colors"
              >
                <ArrowLeft size={16} /> All day trips
              </Link>
              <ShareButton title={`${trip.name} — Day Trip from Copenhagen`} testid="trip-share" />
            </div>
            <span
              className="self-start font-body font-800 text-xs uppercase tracking-[0.3em] eyebrow text-white mb-4"
            >
              {trip.meta.toUpperCase()}
            </span>
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
              className="font-display font-900 tracking-tighter leading-[0.85] text-4xl sm:text-5xl lg:text-6xl text-white"
            >
              {trip.name}
            </motion.h1>
          </div>
        </div>
      </section>

      {/* body */}
      <section className="bg-[#F9F8F6]">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-16 md:py-24 grid grid-cols-1 lg:grid-cols-12 gap-10">
          {/* left: blurb + what to see */}
          <div className="lg:col-span-7">
            <Reveal>
              <div className="font-body text-lg md:text-xl text-black/80 leading-relaxed max-w-2xl space-y-4">
                {trip.blurb.split("\n\n").map((para, i) => (
                  <p key={i}>{para}</p>
                ))}
              </div>
            </Reveal>

            <Reveal delay={0.05}>
              <h2 className="font-display font-900 tracking-tight text-2xl md:text-3xl mt-12 mb-6">What to see</h2>
            </Reveal>
            <div className="border-t border-dotted border-zinc-300">
              {trip.highlights.map((h, i) => (
                <Reveal key={h.name} delay={i * 0.05}>
                  <div className="grid grid-cols-12 gap-3 items-baseline border-b border-zinc-200 py-5 px-2 hover:bg-white transition-colors">
                    <p className="col-span-12 sm:col-span-4 font-display font-900 tracking-tight text-lg">
                      {h.url ? (
                        <a
                          href={h.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid={`trip-highlight-link-${i}`}
                          className="inline-flex items-center gap-1.5 hover:text-[#DF3226] transition-colors"
                        >
                          {h.name} <ArrowUpRight size={16} className="shrink-0" />
                        </a>
                      ) : (
                        h.name
                      )}
                    </p>
                    <p className="col-span-12 sm:col-span-8 font-body text-base text-black/70 leading-relaxed">{h.note}</p>
                  </div>
                </Reveal>
              ))}
            </div>

            {/* getting there */}
            <Reveal delay={0.05}>
              <div className="mt-12 bg-white border border-zinc-200 shadow-brut rounded-2xl p-6 md:p-7" data-testid="trip-getting-there">
                <div className="flex items-center gap-2 mb-4">
                  <Train size={20} style={{ color: trip.accent }} />
                  <h3 className="font-display font-900 tracking-tight text-xl">Getting there</h3>
                </div>
                <ul className="space-y-3">
                  {trip.gettingThere.map((step) => (
                    <li key={step} className="flex gap-3 items-start">
                      <span className="mt-2 w-1.5 h-1.5 rounded-full shrink-0" style={{ backgroundColor: trip.accent }} />
                      <span className="font-body text-base text-black/75 leading-relaxed">{step}</span>
                    </li>
                  ))}
                </ul>
                {trip.tip && (
                  <div className="mt-5 flex gap-2.5 items-start bg-[#F9F8F6] rounded-xl p-4">
                    <Lightbulb size={18} className="text-[#F59E0B] shrink-0 mt-0.5" />
                    <p className="font-body text-sm text-black/70 leading-relaxed">{trip.tip}</p>
                  </div>
                )}
              </div>
            </Reveal>

          </div>

          {/* right: mini map */}
          <div className="lg:col-span-5">
            <Reveal delay={0.1}>
              <div className="lg:sticky lg:top-28">
                <div className="flex items-center gap-2 mb-3">
                  <Train size={16} />
                  <span className="font-body font-800 text-xs tracking-[0.25em]">ON THE MAP</span>
                </div>
                <div className="border border-zinc-200 shadow-brut-lg overflow-hidden h-[380px] md:h-[460px]">
                  <CityMap
                    key={trip.id}
                    center={trip.coords}
                    zoom={12}
                    scrollWheelZoom={false}
                    markers={[{ name: trip.name, coords: trip.coords, color: trip.accent, tag: trip.tag }]}
                  />
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* prev / next */}
      <section className="bg-black text-white border-t border-dotted border-zinc-300">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-10 grid grid-cols-2 gap-4">
          {prev ? (
            <Link to={`/trips/${prev.id}`} data-testid="trip-prev" className="group flex flex-col items-start">
              <span className="font-body font-700 text-xs tracking-widest text-white/50 flex items-center gap-1">
                <ArrowLeft size={14} /> PREVIOUS
              </span>
              <span className="font-display font-900 text-2xl md:text-3xl group-hover:text-[#DF3226] transition-colors">
                {prev.name}
              </span>
            </Link>
          ) : <span />}
          {next && (
            <Link to={`/trips/${next.id}`} data-testid="trip-next" className="group flex flex-col items-end text-right col-start-2">
              <span className="font-body font-700 text-xs tracking-widest text-white/50 flex items-center gap-1">
                NEXT
              </span>
              <span className="font-display font-900 text-2xl md:text-3xl group-hover:text-[#DF3226] transition-colors">
                {next.name}
              </span>
            </Link>
          )}
        </div>
      </section>

      {/* optional bookable tour */}
      {trip.tourId && <GygTourBand key={trip.id} tourId={trip.tourId} />}

      <ShareBar title={`${trip.name} — Day Trip from Copenhagen`} />
    </main>
  );
}
